import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {defineCssVars, defineStyle, defineStyleConfig} from '@chakra-ui/styled-system';
import {transparentize, mode} from '@chakra-ui/theme-tools';

import {semantic as darkSemantic} from '../../design-tokens/dark';
import {semantic} from '../../design-tokens/light';

const vars = defineCssVars(`badge`, [`bg`, `color`, `shadow`]);

const backgroundStateLight = semantic.colors.background.state;
const backgroundStateDark = darkSemantic.colors.background.state;
const textStateLight = semantic.colors.text.state;
const textStateDark = darkSemantic.colors.text.state;
const backgroundNeutralLight = semantic.colors.background.tertiary;
const backgroundNeutralDark = darkSemantic.colors.background.tertiary;
const textNeutralLight = semantic.colors.text.secondary;
const textNeutralDark = darkSemantic.colors.text.secondary;

const baseStyle = defineStyle({
  [vars.bg.variable]: `colors.background.primary`,
  textTransform: `uppercase`,
  bg: vars.bg.reference,
  color: vars.color.reference,
  boxShadow: vars.shadow.reference,
});

const variantSolid = defineStyle(props => {
  const {colorScheme: c, theme} = props;
  const dark = transparentize(`${c}.500`, 0.6)(theme);
  return {
    [vars.bg.variable]: `colors.${c}.500`,
    [vars.color.variable]: `colors.white`,
    _dark: {
      [vars.bg.variable]: dark,
      [vars.color.variable]: `colors.whiteAlpha.800`,
    },
  };
});

const variantBordered = defineStyle(props => {
  const {colorScheme: c} = props;
  const getColorSchemeStyles = (colorScheme: string, props: StyleFunctionProps) => {
    switch (colorScheme) {
      case `white`:
        return {
          bg: `white`,
          border: `solid 1px`,
          borderColor: `gray.200`,
        };

      case `purple`:
        return {
          bg: `electric-indigo.200`,
          border: `solid 1px`,
          borderColor: `electric-indigo.600`,
        };

      case `lightPurple`:
        return {
          bg: `electric-indigo.100`,
          border: `solid 1px`,
          borderColor: `electric-indigo.200`,
        };

      case `red`:
        return {
          bg: `brand.danger.200`,
          border: `solid 1px`,
          borderColor: `brand.danger.500`,
        };

      case `lightRed`:
        return {
          bg: `brand.danger.100`,
          border: `solid 1px`,
          borderColor: `brand.danger.200`,
        };

      case `lightBlue`:
        return {
          bg: `blue-spark.100`,
          border: `solid 1px`,
          borderColor: `blue-spark.200`,
        };

      case `orange`:
        return {
          bg: `brand.warning.200`,
          border: `solid 1px`,
          borderColor: `brand.warning.400`,
        };

      case `yellow`:
        return {
          bg: mode(backgroundStateLight[`warning-subtle`], backgroundStateDark[`warning-subtle`])(props),
          border: `solid 1px`,
          borderColor: `brand.warning.200`,
        };

      case `brand.neutral`:
      default:
        return {
          bg: mode(backgroundNeutralLight, backgroundNeutralDark)(props),
          color: mode(textNeutralLight, textNeutralDark)(props),
        };
    }
  };

  return {
    ...getColorSchemeStyles(c, props),
    borderRadius: `radius-100`,
    px: `space-100`,
    py: `space-050`,
  };
});

const variantSubtle = defineStyle(props => {
  const {colorScheme: c} = props;

  const getColorSchemeStyles = (colorScheme: string, props: StyleFunctionProps) => {
    switch (colorScheme) {
      case `brand.success`:
        return {
          bg: mode(backgroundStateLight[`success-subtle`], backgroundStateDark[`success-subtle`])(props),
          color: mode(textStateLight[`success-bold`], textStateDark[`success-bold`])(props),
        };

      case `brand.danger`:
        return {
          bg: mode(backgroundStateLight[`danger-subtle`], backgroundStateDark[`danger-subtle`])(props),
          color: mode(textStateLight[`danger-bold`], textStateDark[`danger-bold`])(props),
        };

      case `brand.warning`:
        return {
          bg: mode(backgroundStateLight[`warning-subtle`], backgroundStateDark[`warning-subtle`])(props),
          color: mode(textStateLight[`warning-bold`], textStateDark[`warning-bold`])(props),
        };

      case `brand.neutral`:
      default:
        return {
          bg: mode(backgroundNeutralLight, backgroundNeutralDark)(props),
          color: mode(textNeutralLight, textNeutralDark)(props),
        };
    }
  };
  return {
    ...getColorSchemeStyles(c, props),
  };
});

const variantOutline = defineStyle(props => {
  const {colorScheme: c, theme} = props;
  const darkColor = transparentize(`${c}.200`, 0.8)(theme);
  return {
    [vars.color.variable]: `colors.${c}.500`,
    _dark: {
      [vars.color.variable]: darkColor,
    },
    [vars.shadow.variable]: `inset 0 0 0px 1px ${vars.color.reference}`,
  };
});

const variants = {
  solid: variantSolid,
  subtle: variantSubtle,
  outline: variantOutline,
  bordered: variantBordered,
};

export const badgeTheme = defineStyleConfig({
  baseStyle,
  variants,
  defaultProps: {
    variant: `subtle`,
    colorScheme: `gray`,
    size: `md`,
  },
  sizes: {
    lg: {
      px: `space-150`,
      py: `space-050`,
      fontSize: `md`,
      fontWeight: 700,
      borderRadius: `radius-1000`,
    },
    md: {
      px: `space-100`,
      fontSize: `sm`,
      borderRadius: `lg`,
      fontWeight: `normal`,
    },
  },
});

export {vars as badgeVars};
